import {fileTypeFromBuffer} from 'file-type'

export const getMagicNumber = (file, length = 4) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        const blob = file.slice(0, length)

        reader.onloadend = () => {
            if (reader.result instanceof ArrayBuffer) {
                const bytes = new Uint8Array(reader.result)
                const hex = Array.from(bytes)
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join(' ')
                resolve(hex)
            } else {
                reject('Reader result is not an ArrayBuffer')
            }
        }

        reader.onerror = () => reject(reader.error)
        reader.readAsArrayBuffer(blob)
    })
}

export const validateFile = async (file) => {
    const mime = file.type
    const ext = file.name.split('.').pop().toLowerCase()
    const magic = await getMagicNumber(file, 4)

    return checkMimeTypeExtensionAndMagic(mime, ext, magic)
}

const checkMimeTypeExtensionAndMagic = async (mime, ext, magic) => {
    const allowedFileTypes = ['image/jpeg', 'image/png', 'application/pdf']

    if (!allowedFileTypes.includes(mime)) {
        return false
    }

    const sanitizedMagic = magic.replaceAll(' ', '')

    if (mime === 'application/pdf') {
        if (ext === 'pdf' && sanitizedMagic === '25504446') {
            return true
        }
        return false
    } else if (mime === 'image/jpeg') {
        const validJpegMagics = ['ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2']
        if ((ext === 'jpg' || ext === 'jpeg') && validJpegMagics.includes(sanitizedMagic)) {
            return true
        }
    } else if (mime === 'image/png') {
        if (ext === 'png' && sanitizedMagic === '89504e47') {
            return true
        }
    }

    return false
}

/**
 * Compresses an image File to stay under targetSizeMB using the Canvas API.
 * Only runs when the file exceeds thresholdMB.
 *
 * Uses createImageBitmap() instead of new Image() to handle CMYK JPEGs, which
 * cannot be decoded via an <img> element in most browsers.
 *
 * This is necessary because Salesforce MRT runs behind AWS Lambda-like environments, which have a
 * hard ~6 MB synchronous payload limit. Base64 encoding inflates binary size
 * by ~33%, so a 3.5 MB image already exceeds that limit once embedded in JSON.
 *
 * @param {File} file - the file selected by the user
 * @param {number} thresholdMB - only compress if the file is larger than this (default 3)
 * @param {number} targetSizeMB - target maximum size after compression (default 1.5)
 * @returns {Promise<File>} - compressed File, or original if under threshold / decode fails
 */
export const compressImageForUpload = async (file, thresholdMB = 3, targetSizeMB = 1.5) => {
    if (!file || !file.type.startsWith('image/') || file.size <= thresholdMB * 1024 * 1024) {
        return file
    }

    let bitmap
    try {
        bitmap = await createImageBitmap(file)
    } catch (e) {
        console.warn('compressImageForUpload: createImageBitmap failed, returning original', e)
        return file
    }

    const maxBytes = targetSizeMB * 1024 * 1024
    const isJpeg = file.type === 'image/jpeg'
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const attemptCompress = (width, height, quality) =>
        new Promise((resolve) => {
            canvas.width = width
            canvas.height = height
            ctx.drawImage(bitmap, 0, 0, width, height)

            canvas.toBlob(
                async (blob) => {
                    if (!blob) {
                        resolve(file)
                        return
                    }

                    if (blob.size <= maxBytes) {
                        resolve(new File([blob], file.name, {type: file.type}))
                    } else if (isJpeg && quality > 0.25) {
                        resolve(await attemptCompress(width, height, quality - 0.15))
                    } else if (width > 200) {
                        resolve(
                            await attemptCompress(
                                Math.round(width * 0.8),
                                Math.round(height * 0.8),
                                quality,
                            ),
                        )
                    } else {
                        resolve(new File([blob], file.name, {type: file.type}))
                    }
                },
                file.type,
                isJpeg ? quality : undefined,
            )
        })

    const result = await attemptCompress(bitmap.width, bitmap.height, 0.85)
    bitmap.close()
    return result
}

export const validateBase64File = async (base64Data) => {
    const buffer = Buffer.from(base64Data, 'base64')

    const type = await fileTypeFromBuffer(buffer)

    const magicBytes = buffer.slice(0, 4)
    const magic = magicBytes.toString('hex')

    return checkMimeTypeExtensionAndMagic(type?.mime, type?.ext, magic)
}
